import { never as observableNever, interval as observableInterval, Subscription, Observable, Subject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Component, Input, Output, EventEmitter, ViewChild, NgZone, OnInit, OnDestroy } from '@angular/core';
import { InteractivityMediaService, IEMediaCommand } from '../../services/interactivity-media.service';
import { InteractivityEditorService } from '../../services/interactivity-editor.service';
import { MediaclipAsset } from 'app/interfaces/mediaclip';
@Component({
	selector: 'player',
	templateUrl: './player.component.html',
	styleUrls: ['./player.component.scss']
})

export class PlayerComponent implements OnInit, OnDestroy {
	@Input() srcFile: string; // for testing?
	@Input() settings: any;
	@Output() onChange: EventEmitter<any> = new EventEmitter<any>();
	@ViewChild('media', { static: true }) mediaElement: any;

	// These are used in the template
	trustedSrc: string;

	private subscriptions: Subscription;
	private animationSubject: Subject<boolean>;
	private timeUpdateObservable: Observable<number>;
	private animationFrameSubject: Observable<number>;

	private status: any;
	private fps: number;
	private duration: number;

	private frame: number;
	private endedListener: EventListenerOrEventListenerObject;
	private volumeChangeListener: EventListenerOrEventListenerObject;

	private media: HTMLVideoElement | HTMLAudioElement; // private?

	constructor(
		private ngZone: NgZone,
		private interactivityMediaService: InteractivityMediaService,
		private interactivityEditorService: InteractivityEditorService
	) {
		this.settings = {
			autoplay: false
		};

		this.status = {
			playing: false
		};

		this.fps = 25;
		this.interactivityMediaService.fps = this.fps;
		this.frame = 1 / this.fps;

		this.animationSubject = new Subject<boolean>();
		this.timeUpdateObservable = observableInterval(1000 / this.fps);
		this.animationFrameSubject = new Observable<number>();

		this.subscriptions = new Subscription();
		this.subscriptions.add(
			this.interactivityEditorService.mediaclipAssetSubject.subscribe((mediaclipAsset: MediaclipAsset) => {
				this.trustedSrc = mediaclipAsset.src;
				this.duration = parseInt(mediaclipAsset.exactlength, 10) / 1000;
			})
		);
	}

	ngOnInit() {
		this.media = this.mediaElement.nativeElement;

		this.animationFrameSubject = this.animationSubject.pipe(switchMap(
			playing => {
				this.status.playing = !!playing;
				return this.status.playing ? this.timeUpdateObservable : observableNever();
			}
		));

		this.endedListener = () => {
			this.interactivityMediaService.ended = true;
			this.animationSubject.next(false); // not playing
		};
		this.media.addEventListener('ended', this.endedListener);

		this.volumeChangeListener = () => {
			this.interactivityMediaService.volume = this.media.volume;
			this.interactivityMediaService.muted = this.media.muted;
		};
		this.media.addEventListener('volumechange', this.volumeChangeListener);

		this.subscriptions.add(
			this.animationFrameSubject.subscribe(() => this.callAnimationFrame())
		);

		this.subscriptions.add(
			this.interactivityMediaService.commandSubject.subscribe((command: IEMediaCommand) => {
				this.dispatchCommand(command);
			})
		);

		this.subscriptions.add(
			this.interactivityMediaService.volumeSubject.subscribe((volume: number) => {
				if (this.media.volume !== volume) {
					this.media.volume = volume;
				}
			})
		);

		if (this.srcFile) {
			this.trustedSrc = this.srcFile;
		}
	}

	ngOnDestroy() {
		if (this.subscriptions) {
			this.subscriptions.unsubscribe();
		}

		this.media.removeEventListener('volumechange', this.volumeChangeListener);
		this.media.removeEventListener('ended', this.endedListener);
		this.media = null;

		this.animationFrameSubject = null;
		this.timeUpdateObservable = null;
		this.animationSubject = null;
	}

	togglePlayPause() {
		if (!this.status || !this.status.playing) {
			this.play();
		} else {
			this.pause();
		}
	}

	play() {
		const playPromise: Promise<void> = this.media.play();
		if (playPromise !== undefined && playPromise.then) {
			playPromise.then(() => {
				// nothing
			}).catch((reason: any) => {
				// nothing
			});
		}
		this.animationSubject.next(true); // playing
	}

	pause() {
		this.media.pause();
		this.animationSubject.next(false); // not playing
	}

	private dispatchCommand(command: IEMediaCommand) {
		if (!this.media) {
			return;
		}
		let timeOffset = 0;
		switch (command.name) {
			case 'play':
				this.play();
				break;
			case 'pause':
				this.pause();
				break;
			case 'togglePlay':
				this.togglePlayPause();
				break;
			case 'previousFrame':
				this.pause();
				timeOffset = this.media.currentTime - this.frame;
				if (timeOffset < 0) {
					timeOffset = 0;
				}
				this.media.currentTime = timeOffset;
				this.interactivityMediaService.currentTime = this.media.currentTime;
				break;
			case 'nextFrame':
				this.pause();
				timeOffset = this.media.currentTime + this.frame;
				this.media.currentTime = timeOffset;
				this.interactivityMediaService.currentTime = this.media.currentTime;
				break;
			case 'previousSection':
				this.pause();
				this.media.currentTime = 0;
				this.interactivityMediaService.currentTime = this.media.currentTime;
				break;
			case 'nextSection':
				this.pause();
				this.media.currentTime = this.duration;
				this.interactivityMediaService.currentTime = this.media.currentTime;
				break;
			case 'toggleMute':
				this.interactivityMediaService.muted = this.media.muted = !this.media.muted;
				break;
			case 'seekToTime':
				this.media.currentTime = command.time;
				this.interactivityMediaService.currentTime = this.media.currentTime;
				break;
		}
	}

	private callAnimationFrame() {
		this.ngZone.runOutsideAngular(() => requestAnimationFrame(() => {
			this.interactivityMediaService.currentTime = this.media?.currentTime || 0;
		}));
	}
}
